import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_URLS } from '../config/api.urls';

export interface LogisticPartnerListItem {
    id: string;
    carrierType: 'company' | 'private';
    status: 'PENDING' | 'COMPLETED' | 'VERIFIED' | 'REJECTED';
    companyName?: string;
    firstName?: string;
    lastName?: string;
    paymentInfo?: {
        paymentType: 'MOBILE_MONEY' | 'BANK';
        mobileMoneyProvider?: string;
        bankName?: string;
    };
}

export interface LogisticCompanyDetails {
    id: string;
    status: 'PENDING' | 'COMPLETED' | 'VERIFIED' | 'REJECTED';
    reason?: string;
    carrierType: 'company';
    companyName: string;
    legalInfo: {
        rccm: string;
        niu: string;
        businessLicenseNumber: string;
    };
    legalRepresentative: {
        fullName: string;
        position: string;
        phoneNumber: string;
    };
    operationalInfo: {
        companyEmail: string;
        companyPhoneNumber: string;
        country: string;
        city: string;
        address: string;
        description: string;
    };
    paymentInfo: {
        id: string;
        paymentType: 'MOBILE_MONEY' | 'BANK';
        bankName: string | null;
        accountNumber: string | null;
        accountHolderName: string | null;
        mobileMoneyProvider: string | null;
        mobileMoneyNumber: string | null;
        numberVerified: boolean;
    };
    documents: {
        id: string;
        documentType: string;
        documentPath: string;
        deliveredAt?: string;
    }[];
}

export interface LogisticAgentDetails {
    id: string;
    status: 'PENDING' | 'COMPLETED' | 'VERIFIED' | 'REJECTED';
    reason?: string;
    carrierType: 'private';
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    paymentInfo: {
        id: string;
        paymentType: 'MOBILE_MONEY' | 'BANK';
        bankName: string | null;
        accountNumber: string | null;
        accountHolderName: string | null;
        mobileMoneyProvider: string | null;
        mobileMoneyNumber: string | null;
        numberVerified: boolean;
    };
    documents: {
        id: string;
        documentType: string;
        documentPath: string;
    }[];
}

export type LogisticPartnerDetails = LogisticCompanyDetails | LogisticAgentDetails;

@Injectable({
    providedIn: 'root'
})
export class AdminLogisticPartnerService {
    constructor(private http: HttpClient) {}

    /**
     * Get list of all logistic partners
     */
    listPartners(): Observable<{ data: { data: LogisticPartnerListItem[] } }> {
        return this.http.get<{ data: { data: LogisticPartnerListItem[] } }>(API_URLS.ADMIN_LOGISTIC_PARTNERS);
    }

    /**
     * Get detailed information for a specific logistic partner
     */
    getPartnerDetails(partnerId: string): Observable<{ data: LogisticPartnerDetails }> {
        return this.http.get<{ data: LogisticPartnerDetails }>(`${API_URLS.ADMIN_LOGISTIC_PARTNERS}/${partnerId}`);
    }

    /**
     * Approve a logistic partner account
     */
    approvePartner(partnerId: string): Observable<any> {
        return this.http.put(`${API_URLS.ADMIN_LOGISTIC_PARTNERS}/${partnerId}/approveAccount`, {});
    }

    /**
     * Reject a logistic partner account with reason
     */
    rejectPartner(partnerId: string, reason: string): Observable<any> {
        return this.http.put(`${API_URLS.ADMIN_LOGISTIC_PARTNERS}/${partnerId}/rejectAccount`, { reason });
    }
}
